import { SmartKbOnboardingDialog } from "@/components/feature/Modals/SmartKbOnboardingDialog/SmartKbOnboardingDialog"
import { BulkTagDocumentDialog } from "@/components/feature/Modals/TagDocumentDialog/BulkTagDocumentDialog"
import { MainLayout } from "@/components/layout/MainLayout/MainLayout"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useOnboarding } from "@/features/onboarding"
import {
  useGetKnowledgeBaseCategories,
  usePrefetchBulkDocumentTags,
} from "@/hooks/useKnowledgeBaseCategories/useKnowledgeBaseCategories"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useGetKnowledgebaseFiles } from "@/services/api/knowledgebaseService"
import { useTaggingFlowVideoOnboarding } from "@/services/api/userConfigService"
import type { KnowledgebaseFile } from "@/types/knowledgebase"
import { useMemo, useRef, useState } from "react"
import { KnowledgebaseDropOverlay } from "./KnowledgebaseDropOverlay"
import { KnowledgebaseEmptyState } from "./KnowledgebaseEmptyState"
import { KnowledgebaseFileListSection } from "./KnowledgebaseFileListSection"
import { KnowledgebaseSharepointSection } from "./KnowledgebaseSharepointSection"
import { KnowledgebaseUploadBanner } from "./KnowledgebaseUploadBanner"
import { KnowledgebaseUploadButton } from "./KnowledgebaseUploadButton"
import { useKnowledgebaseUpload } from "./useKnowledgebaseUpload"

// Mock files for database tour onboarding
const DEMO_KNOWLEDGEBASE_FILES: KnowledgebaseFile[] = [
  {
    id: "demo-file-1",
    fileName: "Company_Annual_Report_2024.pdf",
    mimetype: "application/pdf",
    createdAt: new Date("2024-01-15"),
    updatedAt: new Date("2024-01-15"),
    expiryDate: null,
    autoTagStatus: null,
    organization: "demo-org",
    source: "API",
    tagIds: [],
  },
  {
    id: "demo-file-2",
    fileName: "Sustainability_Certificate.pdf",
    mimetype: "application/pdf",
    createdAt: new Date("2024-02-20"),
    updatedAt: new Date("2024-02-20"),
    expiryDate: null,
    autoTagStatus: null,
    organization: "demo-org",
    source: "API",
    tagIds: [],
  },
]

const Knowledgebase: React.FC = () => {
  const { translate } = useTranslation()
  const sharepointEnabled = useFeatureFlag("SHAREPOINT")
  const taggingFlowEnabled = useFeatureFlag("KB_TAGGING_FLOW")
  const { hasPermission } = useUser()
  const canUpload = hasPermission(PERMISSIONS.KB_UPLOAD)
  const canManageIntegrations = hasPermission(PERMISSIONS.INTEGRATIONS_MANAGE)
  const { activeTourId, currentStepIndex, getCurrentTour, isActive } =
    useOnboarding()
  const { hasWatched: hasWatchedTaggingFlowVideo, markAsWatched } =
    useTaggingFlowVideoOnboarding()
  const {
    data: filesData,
    isPending: isFilesPending,
    refetch: refetchFiles,
  } = useGetKnowledgebaseFiles()
  const { data: knowledgeBaseCategories } = useGetKnowledgeBaseCategories()

  // Onboarding tour state
  const isDatabaseTourActive = activeTourId === "database"
  const isDatabaseUploadStepActive = useMemo(() => {
    if (!isActive || !isDatabaseTourActive) return false
    const currentTour = getCurrentTour()
    const currentStep = currentTour?.steps?.[currentStepIndex]
    return currentStep?.id === "database-upload-step"
  }, [isActive, isDatabaseTourActive, getCurrentTour, currentStepIndex])

  const isDatabaseFilesStepActive = useMemo(() => {
    if (!isActive || !isDatabaseTourActive) return false
    const currentTour = getCurrentTour()
    const currentStep = currentTour?.steps?.[currentStepIndex]
    return currentStep?.id === "database-files-step"
  }, [isActive, isDatabaseTourActive, getCurrentTour, currentStepIndex])

  const displayFiles = useMemo(
    () => (isDatabaseTourActive ? DEMO_KNOWLEDGEBASE_FILES : (filesData ?? [])),
    [filesData, isDatabaseTourActive],
  )

  // Prefetch tags for every displayed file in ONE request. Row-level
  // `useGetDocumentTags` calls then land as cache hits. Fixes the Sentry N+1
  // where loading the KB page fired ~35 parallel GET /files/<id>/tags calls.
  //
  // Three guards — every one must stay in step with the row-level query:
  //   * `isDatabaseTourActive`: demo set has no backing documents
  //   * `!taggingFlowEnabled`: row-level `useGetDocumentTags` is skipped for
  //     users without the KB_TAGGING_FLOW flag; prefetching anyway would add
  //     one new request per page load for people who can't use tagging.
  const prefetchIds = useMemo(
    () =>
      isDatabaseTourActive || !taggingFlowEnabled
        ? []
        : (filesData ?? []).map((f) => f.id),
    [filesData, isDatabaseTourActive, taggingFlowEnabled],
  )
  usePrefetchBulkDocumentTags(prefetchIds)

  const fileKeys = useMemo(() => {
    return displayFiles.map((data) => data.id).join("")
  }, [displayFiles])

  const spConnectRef = useRef<(() => void) | undefined>(undefined)

  const { uploadingCount, handleFiles, lastUploadedFiles, clearLastUploaded } =
    useKnowledgebaseUpload(refetchFiles)
  const [tagFileIds, setTagFileIds] = useState<string[]>([])

  const handleTagFiles = (fileIds: string[]) => {
    setTagFileIds(fileIds)
    clearLastUploaded()
  }

  return (
    <>
      <MainLayout
        titleInHeader
        title={translate("KNOWLEDGEBASE/TITLE")}
        headerRightElement={
          <div className="flex items-center gap-2">
            {sharepointEnabled &&
              !isDatabaseTourActive &&
              canManageIntegrations && (
                <KnowledgebaseSharepointSection
                  filesData={filesData}
                  refetchFiles={refetchFiles}
                  isDatabaseTourActive={isDatabaseTourActive}
                  connectHandlerRef={spConnectRef}
                  compact
                />
              )}
            <KnowledgebaseUploadButton
              uploadingCount={uploadingCount}
              onFilesSelected={handleFiles}
              isDisabled={isDatabaseUploadStepActive}
            />
          </div>
        }
        contentCenter={
          <>
            <div className="flex h-full min-w-0 flex-col justify-start overflow-hidden">
              {taggingFlowEnabled && lastUploadedFiles.length > 0 && (
                <div className="mb-4">
                  <KnowledgebaseUploadBanner
                    uploadedFiles={lastUploadedFiles}
                    onTagFiles={handleTagFiles}
                    onDismiss={clearLastUploaded}
                  />
                </div>
              )}
              {filesData?.length === 0 && !isDatabaseTourActive ? (
                <KnowledgebaseEmptyState
                  key={fileKeys}
                  uploadingCount={uploadingCount}
                  onFilesSelected={handleFiles}
                  onConnectSharePoint={
                    canManageIntegrations
                      ? () => spConnectRef.current?.()
                      : undefined
                  }
                  canUpload={canUpload}
                />
              ) : (
                <KnowledgebaseFileListSection
                  displayFiles={displayFiles}
                  isFilesPending={isFilesPending}
                  refetchFiles={refetchFiles}
                  filterCategories={knowledgeBaseCategories ?? []}
                  isDatabaseTourActive={isDatabaseTourActive}
                  isDatabaseFilesStepActive={isDatabaseFilesStepActive}
                />
              )}
            </div>
          </>
        }
      />
      {canUpload && (
        <KnowledgebaseDropOverlay
          onFilesDropped={handleFiles}
          isDisabled={isDatabaseUploadStepActive}
        />
      )}
      <BulkTagDocumentDialog
        open={tagFileIds.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setTagFileIds([])
            refetchFiles()
          }
        }}
        fileIds={tagFileIds}
      />
      {taggingFlowEnabled ? (
        <SmartKbOnboardingDialog
          open={!hasWatchedTaggingFlowVideo}
          onOpenChange={(open) => {
            if (!open) markAsWatched()
          }}
          onComplete={markAsWatched}
        />
      ) : null}
    </>
  )
}

export default Knowledgebase
